import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { debounce } from "lodash";
// @mui
import { Container, Typography, Stack, Chip, Switch, FormControlLabel, TextField, Button, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
// components
import { useSettingsContext } from '../../components/settings';
import CustomPagination from '../../components/custom-pagination';
import Iconify from '../../components/iconify';
import CustomModal from "../../components/custom-modal";
import CustomGrid from '../../components/custom-grid';
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { getPortalSymptoms,onPortalUpdateSymptomStatus,onPortalUpdateSymptoms } from '../../APIs/symptoms';
import {getSymptomsListColumns} from './SymptomsColumns'
import {SymptomsView} from './SymptomsView'

// ----------------------------------------------------------------------
import { useAuthContext } from '../../auth/useAuthContext';

export default function SymptomsList() {
    const { themeStretch } = useSettingsContext();
    const axiosPrivate = useAxiosPrivate();    
    const [includeOnlyActive, setIncludeOnlyActive] = useState(false);
    const [totalSymptoms, setTotalSymptoms] = useState(0);
    const [symptoms, setSymptoms] = useState([]);    
    const [term, setTerm] = useState();
    const [fullWidth] = useState(true);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [openModal, setOpenModal] = useState(false);
    const [selectedSymptoms, setSelectedSymptoms] = useState();
    const [readOnly, setReadOnly] = useState(true);
    
    const getSymptoms = (currentPage = 1) => {
        const request = { includeOnlyActive,term, size, page: currentPage ?? page }
        setPage(request.page);

        getPortalSymptoms(axiosPrivate, request, (response) => {
            const { data, total } = response;
            setSymptoms(data);
            setTotalSymptoms(total);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getSymptoms, [includeOnlyActive,term, size]);    

    const handleChange = (event) => {
        setIncludeOnlyActive(event.target.checked);
    };

    const handleSearch = (event) => {
        setTerm(event.target.value);
    }

    const handlePageChange = (pageIndex) => {
        setPage(pageIndex);
        getSymptoms(pageIndex);
    }

    const handlePageSizeChange = (pageSize) => {
        setSize(pageSize);
    }

    const onHandleClose = () => {
        setSelectedSymptoms();   
        setReadOnly(true);     
        setOpenModal(false);
    }

    const handleRowClick = ({ row }) => {
        setSelectedSymptoms(row);
        setOpenModal(true);
        setReadOnly(true);
    }

    const onEdit = (id) => {        
        const _symptoms = symptoms.find(c => c.id === id);
        setSelectedSymptoms(_symptoms);
        setReadOnly(false);
        setOpenModal(true);
    }

    const onAdd = () => {
        setReadOnly(false);
        setOpenModal(true);
    }

    const onDeactive = (id) => {
        onPortalUpdateSymptomStatus(axiosPrivate, { id, isActive: false }, (response) => {
            if (response.status === 200) {
                getSymptoms(page);
            }
        });
    }

    const onActive = (id) => {
        onPortalUpdateSymptomStatus(axiosPrivate, { id, isActive: true }, (response) => {
            if (response.status === 200) {
                getSymptoms(page);
            }
        });
    }

    const handleUser = () => {
        getSymptoms(page);
        onHandleClose();
    }

    const debouncedChangeHandler = debounce(handleSearch, 500);

    const columns = getSymptomsListColumns({ onEdit, onActive, onDeactive  });

    return (
        <>
            <Helmet>
                <title>Symptoms | flok</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'xl'}>
                <Stack
                    flexGrow={1}
                    direction="column"
                    justifyContent="flex-start"
                    spacing={{ xs: 0.5, sm: 1.5 }}>
                    <Typography variant="h4" component="h4" paragraph>
                    Symptoms <Chip label={totalSymptoms} />
                        <Button style={{ marginLeft: '2%', minWidth: 0 }} variant="outlined" onClick={onAdd}>+</Button>
                    </Typography>
                    <Stack
                        flexGrow={1}
                        direction="row"
                        justifyContent="flex-start"
                        spacing={{ xs: 0.5, sm: 1.5 }}>
                        <TextField
                            hiddenLabel
                            placeholder="Search..."
                            size="small"
                            onChange={debouncedChangeHandler}
                            fullWidth={fullWidth}
                            defaultValue={term}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeOnlyActive}
                                    onChange={handleChange}
                                    inputProps={{ 'aria-label': 'controlled' }} />
                            }
                            label="Hide Deleted Diagnosis" />
                    </Stack>
                    <CustomGrid>
                        <DataGrid
                            sx={{
                                '.MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'lightgray'
                                },
                                '.MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                                '&.MuiDataGrid-root': {
                                    border: 'none'
                                },
                                '.MuiDataGrid-row': {
                                    cursor: 'pointer'
                                },
                                '.MuiDataGrid-footerContainer': {
                                    display: 'none'
                                }
                            }}
                            rows={symptoms}
                            columns={columns}
                            disableColumnMenu
                            disableColumnFilter
                            disableRowSelectionOnClick
                            hideFooterPagination
                            onRowClick={handleRowClick}
                        />
                    </CustomGrid>
                    <CustomPagination pageSize={size} currentPage={page} total={totalSymptoms} handleChange={handlePageChange} handlePageSizeChange={handlePageSizeChange} />
                    <CustomModal open={openModal} onClose={onHandleClose}>
                        <SymptomsView item={selectedSymptoms} onHandleClose={onHandleClose} readOnly={readOnly} onChange={handleUser}/>
                    </CustomModal>
                </Stack>
            </Container>
        </>
    );
}

